import {ChangeDetectorRef, Component, ContentChild, Input, OnDestroy, OnInit, TemplateRef} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {
  ParsedCmsFlexComponentData
} from '../order-confirmation/order-confirmation-list-item/order-confirmation-order-item.model';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {DcpCartHistoryService} from '../services/dcp-cart-history.service';
import {Router} from '@angular/router';
import {Address} from '@spartacus/core';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {Vendor} from '@app/model/vendor.model';
import {DcpCartService} from '@app/product/services/dcp-cart.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

/**
 * This component creates a provided by vendor Label and sorts the OrderEntries passed into it by their vendor.
 * It uses ng-content and expects a ng-template as Content root to pass the current Order entry into it:
 * <ng-template #providedOrderEntries let-context>
 * Otherwise an exception will be thrown.
 */
@UntilDestroy()
@Component({
  selector: 'dcp-provided-by',
  templateUrl: './provided-by.component.html',
  styleUrls: ['./provided-by.component.scss']
})
export class ProvidedByComponent implements OnInit, OnDestroy {

  @ContentChild('derivatives') derivativesTmpl: TemplateRef<any>;
  @ContentChild('groupTotal') groupTotalTmpl: TemplateRef<any>;
  @ContentChild('recurringTotal') recurringTotalTmpl: TemplateRef<any>;
  @ContentChild('providedOrderEntries') providedOrderEntriesTmpl: TemplateRef<any>;
  @ContentChild('payment') paymentTmpl: TemplateRef<any>;
  @ContentChild('termsAndConditions') termsAndConditionsTmpl: TemplateRef<any>;
  @Input() orderEntries$: Observable<OrderEntry[]>;
  @Input() resetOrderEntries: boolean = false;
  public providerNames: string[] = [];
  public entryCount: number;
  public sortedEntries: OrderEntry[];
  public componentData$: Observable<ParsedCmsFlexComponentData>;
  public sortedEntriesSubscription: Subscription = Subscription.EMPTY;
  public orderEntriesChangedSubscription: Subscription = Subscription.EMPTY;
  private cartSubscription: Subscription = Subscription.EMPTY;

  constructor(
    protected router: Router,
    protected cartHistoryService: DcpCartHistoryService,
    protected changeRef: ChangeDetectorRef,
    protected cartService: DcpCartService,
    protected cartDecoratorService: DcpCartDecoratorService
  ) {
  }

  ngOnInit(): void {
    this.subscribeToSortedEntries();
    this.cartDecoratorService.isPlaceOrderPending$.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value) {
        this.cartDecoratorService.providedByOrderPlaced = true;
      }
    });
    this.manageCartSubscription();
  }

  subscribeToSortedEntries() {
    if (this.sortedEntriesSubscription.closed && this.orderEntries$) {
      this.sortedEntriesSubscription = this.orderEntries$.pipe(untilDestroyed(this)).subscribe((entries) => {
        this.redirectToCartPageIfNecessary(entries ? entries.length : 0);
        if (this.resetOrderEntries) {
          this.cartHistoryService.innerOrderEntries = [];
        }
        this.cartHistoryService.orderEntries = this.sortEntriesByProvidersName(entries);
        this.sortedEntries = this.cartHistoryService.innerOrderEntries;
        this.prepareProvidersNames(this.sortedEntries);
        this.entryCount = this.sortedEntries.length;
        this.changeRef.detectChanges();
      });
    }

    if (this.orderEntriesChangedSubscription.closed) {
      this.orderEntriesChangedSubscription = this.cartHistoryService.orderEntriesChanged.pipe(untilDestroyed(this)).subscribe(() => {
        this.changeRef.detectChanges();
      });
    }
  }

  /**
   * Redirect to Cart Page when last cart entry is removed (DCP-3492)
   * providedByOrderPlaced is used prevent redirection to Cart Page, after we place an order and cart is cleared
   */
  redirectToCartPageIfNecessary(orderEntriesCount: number) {
    if (orderEntriesCount > 0) {
      this.cartDecoratorService.providedByOrderPlaced = false;
    } else if (!this.cartDecoratorService.providedByOrderPlaced) {
      setTimeout(() => {
        this.router.navigate(['/cart']);
      });
    }
  }

  ngOnDestroy(): void {
    this.cartSubscription.unsubscribe();
    this.sortedEntriesSubscription.unsubscribe();
    this.orderEntriesChangedSubscription.unsubscribe();
  }

  /**
   * Subscribes or unsubscribes to cart changes when the edit modal is opened.
   * If the cart is refreshed while the modal is opened the UI will be refreshed.
   * While refreshing the UI, the modal component will be destroyed and the modal will be closed.
   * This can be prevented by unsubscribing while the modal is opened.
   */
  manageCartSubscription() {
    this.cartSubscription = this.cartService.cartSubscriptionStatus
      .pipe(untilDestroyed(this))
      .subscribe(s => {
        if (s === 'subscribe') {
          this.subscribeToSortedEntries();
        } else {
          this.sortedEntriesSubscription.unsubscribe();
          this.orderEntriesChangedSubscription.unsubscribe();
        }
      });
  }

  getProductVendor(entry: OrderEntry): Vendor {
    return entry?.product?.vendor;
  }

  getProductVendorName(entry: OrderEntry): string {
    return this.getProductVendor(entry)?.name || '';
  }

  getProductVendorAddress(entry: OrderEntry): Address {
    return this.getProductVendor(entry)?.address;
  }

  sortEntriesByProvidersName(entries: OrderEntry[]): OrderEntry[] {
    if (entries?.length) {
      return entries.slice().sort((a, b) =>
        this.getProductVendorName(a).localeCompare(this.getProductVendorName(b))
      );
    }
    return [];
  }

  isNeedToShowProvider(index: number, entry: OrderEntry): boolean {
    return this.providerNames.indexOf(this.getProductVendorName(entry)) === index;
  }

  /**
   * Returns next entry's index
   * or given index for last entry
   *
   * @param index current entry's index
   * @return next entry's index
   */
  public getNextEntryIndex(index: number): number {
    const nextIndex: number = index + 1;
    return nextIndex < this.entryCount ? nextIndex : index;
  }

  /**
   * Adds provider's names to the list of names
   */
  prepareProvidersNames(entries: OrderEntry[]): void {
    entries.forEach(e => {
      const providerName: string = this.getProductVendorName(e);
      this.providerNames.push(providerName);
    });
  }
}
